import { useEffect, useRef, useState } from 'react';
import { useOnline } from '@/hooks/useOnline';
import { cn } from '@/lib/cn';

/**
 * Shows a banner when the device goes offline (after a 2s debounce) and
 * briefly flashes "Back online" for 3s once connectivity returns.
 */
export function OfflineBanner() {
  const online = useOnline();
  const [show, setShow] = useState(false);
  const wasOfflineRef = useRef(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout> | undefined;

    if (!online) {
      wasOfflineRef.current = true;
      // Debounce — don't flash on brief blips
      showTimer = setTimeout(() => {
        setShow(true);
      }, 2000);
    } else if (wasOfflineRef.current) {
      // Reconnected: show "Back online" immediately, then auto-dismiss
      wasOfflineRef.current = false;
      showTimer = setTimeout(() => {
        setShow(true);
      }, 0);
      dismissTimerRef.current = setTimeout(() => {
        setShow(false);
      }, 3000);
    }

    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [online]);

  if (!show) return null;

  const message = online ? 'Back online' : 'No connection — changes saved locally';

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'fixed inset-x-0 top-[env(safe-area-inset-top)] z-float flex h-8 items-center justify-center text-xs font-medium',
          'animate-in slide-in-from-top duration-modal ease-out-strong',
          'motion-reduce:animate-none',
          online ? 'bg-ok-bg text-t1' : 'bg-warn-bg text-t1',
        )}
      >
        {message}
      </div>
      {/* Reserve layout height only while persistently offline, so the topbar
          is pushed below the fixed bar instead of being overlapped. The
          transient "Back online" flash keeps overlaying (no spacer) to avoid a
          push-down/jump-back layout shift as it appears and auto-dismisses. */}
      {!online && <div className="h-8 shrink-0" aria-hidden />}
    </>
  );
}
